// components
import { Icon } from '@sdb/planning-ui';
import Notes from 'components/employee-portal/notes/Notes';
import { UpdateMatchInfoDialog } from './UpdateMatchInfoDialog';

// hooks
import { useGetClustersPreferred } from 'queries';
import { useTranslation } from 'react-i18next';
import useGetRequestQualifications from '../hooks/useGetRequestQualifications';

// styles
import { StyledBoxFullWidth } from 'components/shared/Box/Box.styles';
import { IconContainer } from 'components/shared/Header/Header.styles';
import {
  StyledTypographyDisplayBlock,
  StyledTypographyLineClamped
} from 'components/shared/Typography/Typography.styles';
import { StyledTopMatchInfoContainer } from './MatchDrawer.styles';
import {
  StyledBottomInfoBox,
  StyledCustomMatchDrawerTooltip,
  StyledCustomWidthBox,
  StyledIconButton,
  StyledIconButtonSmall,
  StyledMatchInfoTemplateWrapper,
  StyledTopMatchInfoBlock,
  StyledTopMatchMainContent
} from './MatchDrawerInfoContent.styles';

// types
import { NotesTypeEnums } from 'components/employee-portal/notes/Notes.types';
import type { FlexMatchTaskResponse } from 'RestAPIs/flex-match/types';
import type {
  MatchDrawerInfoUpdateType,
  MatchInfo
} from './MatchDrawerInfoContent.types';

// utils
import dayjs from 'dayjs';
import { useState } from 'react';

export type MatchDrawerInfoContentProps = {
  selectedRow?: FlexMatchTaskResponse | null;
  handleInfoClick?: (row: FlexMatchTaskResponse) => void;
  handleClose?: () => void;
  handleMatchDrawerClose?: () => void;
};

export const MatchDrawerInfoContent = ({
  selectedRow,
  handleInfoClick,
  handleMatchDrawerClose
}: MatchDrawerInfoContentProps) => {
  'use memo';

  const { t } = useTranslation('common', {
    keyPrefix: 'flexMatch'
  });
  const { t: tCommon } = useTranslation('common');
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [updateType, setUpdateType] = useState<MatchDrawerInfoUpdateType>(null);
  const { data: preferredClusters } = useGetClustersPreferred(
    selectedRow?.costCenterId || -1,
    {
      enabled: !!selectedRow?.costCenterId
    }
  );

  const { data: requestQualifications } = useGetRequestQualifications(
    { requestId: selectedRow?.requestId },
    { enabled: !!selectedRow?.requestId }
  );

  if (!selectedRow) return null;

  const TooltipMatchInfo = (value?: string) => {
    if (!value) return '-';
    return (
      <StyledCustomMatchDrawerTooltip arrow title={value}>
        <StyledTypographyDisplayBlock component="span" variant="body2">
          {value}
        </StyledTypographyDisplayBlock>
      </StyledCustomMatchDrawerTooltip>
    );
  };

  const clusterNames = preferredClusters
    ?.map((cluster) => cluster.clusterName)
    .join(', ');

  const qualificationNames = requestQualifications
    ?.map((qualification) => qualification.name)
    .join(', ');

  const clusters =
    !!preferredClusters?.length && TooltipMatchInfo(clusterNames);
  const qualifications =
    !!requestQualifications?.length && TooltipMatchInfo(qualificationNames);

  const handleUpdateDialog = (type: MatchDrawerInfoUpdateType) => {
    setIsUpdateDialogOpen(true);
    setUpdateType(type);
  };

  const handleUpdateDialogOnClose = () => {
    setIsUpdateDialogOpen(false);
    setUpdateType(null);
  };

  const matchInfo: MatchInfo[] = [
    {
      label: tCommon('date'),
      value: dayjs(selectedRow?.date).format('DD-MM-YYYY')
    },
    {
      label: tCommon('costCenter'),
      value: selectedRow?.costCenterName || '-',
      actionButton: {
        action: () => handleInfoClick?.(selectedRow),
        icon: 'circle-info',
        testId: 'cost-center-info-button'
      },
      tooltip: {
        icon: 'note',
        value: selectedRow?.remarks
      }
    },
    {
      label: tCommon('period'),
      value:
        selectedRow?.periods?.length > 0
          ? `${dayjs(selectedRow?.periods[0].startTime).format(
              'HH:mm'
            )} - ${dayjs(
              selectedRow?.periods[selectedRow?.periods.length - 1].endTime
            ).format('HH:mm')}`
          : '-'
    },
    {
      label: tCommon('dutyCode'),
      value: selectedRow?.displayDutyCode || '-',
      updateSection: {
        action: () => handleUpdateDialog?.('dutyCodeAndFunction'),
        icon: 'pen',
        testId: 'update-duty-code-and-function-button'
      }
    },
    {
      label: tCommon('function'),
      value: selectedRow?.functionName || '-',
      updateSection: {
        action: () => handleUpdateDialog?.('dutyCodeAndFunction'),
        icon: 'pen'
      }
    },
    {
      label: t('columns.sendToFlex'),
      value: dayjs(selectedRow?.sendToFlex).format('DD-MM-YYYY HH:mm')
    },
    {
      label: tCommon('reason'),
      value: selectedRow?.reason || '-'
    },
    {
      label: t('matchDrawer.applicant'),
      value: selectedRow?.planner || '-'
    },
    {
      label: tCommon('cluster'),
      value: clusters
    },
    {
      label: tCommon('qualifications'),
      value: qualifications,
      updateSection: {
        action: () => handleUpdateDialog?.('qualifications'),
        icon: 'pen'
      }
    }
  ];

  const MatchInfoTemplate = (info: MatchInfo) => {
    return (
      <StyledMatchInfoTemplateWrapper>
        <StyledTypographyLineClamped
          lineClamp={1}
          variant="body2"
          color="text.secondary"
        >
          {info.label}
          {info.updateSection && (
            <StyledIconButtonSmall
              onClick={info.updateSection.action}
              data-testid={info.updateSection.testId}
            >
              <IconContainer>
                <Icon type={info.updateSection.icon} color="inherit" />
              </IconContainer>
            </StyledIconButtonSmall>
          )}
        </StyledTypographyLineClamped>
        <StyledBottomInfoBox>
          <StyledTypographyLineClamped
            lineClamp={1}
            variant="body2"
            color="text.primary"
          >
            {info.value}
          </StyledTypographyLineClamped>
          {info.actionButton && (
            <StyledIconButton
              onClick={info.actionButton.action}
              data-testid={info.actionButton.testId}
            >
              <IconContainer>
                <Icon type={info.actionButton.icon} color="inherit" />
              </IconContainer>
            </StyledIconButton>
          )}
          {info.tooltip && (
            <StyledCustomMatchDrawerTooltip title={info.tooltip.value} arrow>
              <IconContainer>
                <Icon type={info.tooltip.icon} />
              </IconContainer>
            </StyledCustomMatchDrawerTooltip>
          )}
        </StyledBottomInfoBox>
      </StyledMatchInfoTemplateWrapper>
    );
  };

  return (
    <>
      <StyledTopMatchInfoContainer>
        <StyledTopMatchMainContent>
          <StyledTopMatchInfoBlock>
            {matchInfo.map((info, index) => (
              <MatchInfoTemplate key={index} {...info} />
            ))}
          </StyledTopMatchInfoBlock>
          <StyledCustomWidthBox>
            <StyledBoxFullWidth>
              <Notes
                requestId={selectedRow?.requestId}
                size="compact"
                type={NotesTypeEnums.FLEX_MATCH_REQUEST}
                noteVariant="alternative"
                showTooltip
              />
            </StyledBoxFullWidth>
          </StyledCustomWidthBox>
        </StyledTopMatchMainContent>
      </StyledTopMatchInfoContainer>
      <UpdateMatchInfoDialog
        isOpen={isUpdateDialogOpen}
        onClose={handleUpdateDialogOnClose}
        selectedRow={selectedRow}
        updateType={updateType}
        handleMatchDrawerClose={handleMatchDrawerClose}
        requestQualifications={requestQualifications ?? []}
      />
    </>
  );
};
